import { Service, TenantBusiness } from '@turizi/shared';
import { useTranslation } from 'react-i18next';
import { Badge, Button, Select, SegmentedControl, Tooltip, Modal } from '@mantine/core';
import { getAttrValue, getServiceSellCost } from '@/lib/models-utils';
import { useState, useMemo, useEffect, useCallback } from 'react';
import { IconChevronUp, IconChevronDown, IconSelector, IconEdit, IconList, IconTable, IconX, IconDiscount } from '@tabler/icons-react';
import { formatDayShort, formatPrice, formatTime, getUrlParam, todayStart, updateUrlParams } from '@/lib/format-utils';
import { getServiceTableView, setServiceTableView } from '@/services/user-config-service';
import { getServiceSellPrice } from '@/lib/models-utils';
import SearchInput from '../search-input';
import { BUSINESSES_PATH, SERVICES_PATH } from '@/lib/navigation';
import { Link } from 'react-router-dom';
import { SERVICE_TEMPLATES } from '@/types/templates';
import ServiceFiltersColumn from './service-filters-column';
import ServiceCardPrivate from '../service-card-private';
import BulkServiceEditModal from './bulk-service-edit-modal';
import CustomCheckbox from '../custom-checkbox';
import { ServiceTitleStatic } from '../services/service-title';
import ServiceCard from '../service-card-public';
import { multiWordSearch } from '@/utils/search-utils';


interface Props {
    client?: TenantBusiness;
    services: Service[];
    onSelect?: (service: Service) => void;
    dates?: [string, string];
    showAllSelfAttrs?: boolean; // Whether to show all self_attrs
    excluded?: string[]; // List of self_attr keys to exclude from display
    hideCityInTable?: boolean;
    sortBy?: string;
    publicPage?: boolean;
}

export default function ServiceTableGeneral({
    client,
    services,
    onSelect,
    dates,
    showAllSelfAttrs = true,
    excluded = [],
    hideCityInTable = false,
    sortBy,
    publicPage = false,
}: Props) {
    const { t } = useTranslation();
    const isMobile = window.screen.width < 768;
    const [filtersModalOpened, setFiltersModalOpened] = useState(false);

    const [view, setView] = useState<'table' | 'list'>(() => {
        const urlView = getUrlParam('view', '');
        if (urlView) return urlView as 'table' | 'list';
        return getServiceTableView() || 'list';
    });
    const [selectedServices, setSelectedServices] = useState<number[]>([]);
    const [searchText, setSearchText] = useState(getUrlParam('search', ''));
    const [sortConfig, setSortConfig] = useState<{ key: string | null; direction: 'asc' | 'desc' | null }>({
        key: sortBy, direction: 'asc'
    });

    const handleSelectService = (serviceId: number, checked: boolean) => {
        setSelectedServices(prev => {
            if (checked) {
                return [...prev, serviceId];
            }
            return prev.filter(id => id !== serviceId);
        });
    };

    const handleSelectAllServices = (checked: boolean) => {
        setSelectedServices(checked ? filteredAndSortedServices.map(service => service.id) : []);
    };

    const allAttributeKeys = useMemo(() => {
        const attributeMap = new Map<string, { key: string; sort: number }>();

        if (showAllSelfAttrs) {
            // Get the service category from the first service
            const serviceCategory = services[0]?.category;

            // Get the template for this category
            const template = serviceCategory ? SERVICE_TEMPLATES[serviceCategory as keyof typeof SERVICE_TEMPLATES] : null;

            // Extract attributes from the template instead of from the services
            if (template?.self_attrs) {
                template.self_attrs.forEach(attr => {
                    if (attr.key &&
                        !attributeMap.has(attr.key) &&
                        !excluded.includes(attr.key)) {
                        attributeMap.set(attr.key, {
                            key: attr.key,
                            sort: attr.sort ?? 999999 // Default high sort value for attrs without sort
                        });
                    }
                });
            }
        }

        // Sort by sort field and return keys
        return Array.from(attributeMap.values())
            .sort((a, b) => a.sort - b.sort)
            .map(item => item.key);
    }, [services, showAllSelfAttrs, excluded]);

    // Extract provider attribute keys
    const { providerAttributeKeys } = useMemo(() => {
        const providerAttrMap = new Map<string, { key: string; sort: number }>();
        const providerNameCount = new Map<string, number>();

        services.forEach(service => {
            service.providers?.forEach(provider => {
                // Count provider names to find the most common
                if (provider.name) {
                    providerNameCount.set(provider.name, (providerNameCount.get(provider.name) || 0) + 1);
                }

                provider.self_attrs?.forEach(attr => {
                    if (
                        attr.key &&
                        !excluded.includes(attr.key) &&
                        !providerAttrMap.has(attr.key)
                    ) {
                        providerAttrMap.set(attr.key, {
                            key: attr.key,
                            sort: attr.sort ?? 999999
                        });
                    }
                });
            });
        });

        // Sort by sort field and return keys
        const keys = Array.from(providerAttrMap.values())
            .sort((a, b) => a.sort - b.sort)
            .map(item => item.key);

        return { providerAttributeKeys: keys };
    }, [services, excluded]);

    // Initialize filters state dynamically based on attribute keys
    const initialFilters = useMemo(() => {
        const filters: Record<string, any[]> = {};
        allAttributeKeys.forEach(key => {
            filters[key] = [];
        });
        providerAttributeKeys.forEach(key => {
            filters[`provider_${key}`] = [];
        });
        filters.businessName = [];
        filters.sellerName = [];
        filters.publishPrice = [];

        // Load filters from URL
        const filtersParam = getUrlParam('filters', '');
        if (filtersParam) {
            try {
                const urlFilters = JSON.parse(decodeURIComponent(filtersParam));
                Object.keys(urlFilters).forEach(key => {
                    if (filters.hasOwnProperty(key)) {
                        filters[key] = urlFilters[key];
                    }
                });
            } catch (e) {
                console.error('Failed to parse filters from URL:', e);
            }
        }

        return filters;
    }, [allAttributeKeys, providerAttributeKeys]);

    const [filters, setFilters] = useState(initialFilters);

    useEffect(() => {
        // Check if filters have any active values
        const hasActiveFilters = Object.values(filters).some(filterValues => filterValues.length > 0);

        updateUrlParams({
            view: view,
            search: searchText,
            filters: hasActiveFilters ? encodeURIComponent(JSON.stringify(filters)) : undefined
        });
        setServiceTableView(view);
    }, [view, searchText, filters]);

    // Extract filter options from services (base options - all available)
    const allFilterOptions = useMemo(() => {
        const options: Record<string, Set<any>> = {};

        // Initialize sets for each attribute
        allAttributeKeys.forEach(key => {
            options[key] = new Set();
        });
        providerAttributeKeys.forEach(key => {
            options[`provider_${key}`] = new Set();
        });
        options.businessName = new Set<string>();
        options.sellerName = new Set<string>();
        options.publishPrice = new Set<boolean>();

        services.forEach(service => {
            // Extract service attribute values
            allAttributeKeys.forEach(key => {
                const value = getAttrValue(service, key);
                if (value !== null && value !== undefined && value !== '') {
                    options[key].add(value);
                }
            });

            // Extract provider attribute values
            service.providers?.forEach(provider => {
                // Add business name
                if (provider.name) options.businessName.add(provider.name);

                // Add provider self_attrs
                provider.self_attrs?.forEach(attr => {
                    if (attr.key && attr.value !== null && attr.value !== undefined && attr.value !== '') {
                        const optionKey = `provider_${attr.key}`;
                        if (options[optionKey]) {
                            options[optionKey].add(attr.value);
                        }
                    }
                });
            });

            // Extract seller name
            if (service.seller?.name) {
                options.sellerName.add(service.seller.name);
            }

            // Extract publish price values
            if (service.publish_price !== null && service.publish_price !== undefined) {
                options.publishPrice.add(service.publish_price);
            }
        });

        // Convert sets to sorted arrays
        const sortedOptions: Record<string, any[]> = {};
        Object.entries(options).forEach(([key, valueSet]) => {
            const values = Array.from(valueSet);
            // Sort numbers numerically, everything else alphabetically
            if (values.every(v => !isNaN(Number(v)))) {
                sortedOptions[key] = values.sort((a, b) => Number(a) - Number(b));
            } else {
                sortedOptions[key] = values.sort();
            }
        });

        return sortedOptions;
    }, [services, allAttributeKeys, providerAttributeKeys]);

    // Compute available filter options based on currently selected filters
    // For public pages, skip the expensive dynamic filtering to improve performance
    const availableFilterOptions = useMemo(() => {
        // For public pages, just return all options to avoid expensive calculations
        if (publicPage) {
            return allFilterOptions;
        }

        // Check if any filters are active
        const hasActiveFilters = Object.values(filters).some(filterValues => filterValues.length > 0);

        // If no filters are active, return all options
        if (!hasActiveFilters) {
            return allFilterOptions;
        }

        const options: Record<string, Set<any>> = {};

        // Initialize sets for each attribute
        allAttributeKeys.forEach(key => {
            options[key] = new Set();
        });
        providerAttributeKeys.forEach(key => {
            options[`provider_${key}`] = new Set();
        });
        options.businessName = new Set<string>();
        options.sellerName = new Set<string>();
        options.publishPrice = new Set<boolean>();

        // For each filter type, compute what values are available given OTHER filter selections
        const allFilterKeys = [
            'businessName',
            'sellerName',
            'publishPrice',
            ...allAttributeKeys,
            ...providerAttributeKeys.map(key => `provider_${key}`)
        ];

        allFilterKeys.forEach(currentFilterKey => {
            // Create a temporary filter set that excludes the current filter
            const otherFilters = { ...filters };
            delete otherFilters[currentFilterKey];

            // Filter services based on OTHER filters only
            const filteredServices = services.filter(service => {
                for (const [key, filterValues] of Object.entries(otherFilters)) {
                    if (filterValues.length > 0) {
                        if (key === 'businessName') {
                            const hasMatchingBusiness = service.providers?.some(provider =>
                                filterValues.includes(provider.name)
                            );
                            if (!hasMatchingBusiness) return false;
                        } else if (key === 'sellerName') {
                            if (!filterValues.includes(service.seller?.name)) return false;
                        } else if (key === 'publishPrice') {
                            if (!filterValues.includes(service.publish_price)) return false;
                        } else if (key.startsWith('provider_')) {
                            const attrKey = key.replace('provider_', '');
                            const hasMatchingProvider = service.providers?.some(provider => {
                                const providerAttr = provider.self_attrs?.find(attr => attr.key === attrKey);
                                return providerAttr && filterValues.includes(providerAttr.value);
                            });
                            if (!hasMatchingProvider) return false;
                        } else {
                            const attrValue = getAttrValue(service, key);
                            if (!filterValues.includes(attrValue)) return false;
                        }
                    }
                }
                return true;
            });

            // Extract available values for the current filter from the filtered services
            filteredServices.forEach(service => {
                if (currentFilterKey === 'businessName') {
                    service.providers?.forEach(provider => {
                        if (provider.name) options.businessName.add(provider.name);
                    });
                } else if (currentFilterKey === 'sellerName') {
                    if (service.seller?.name) {
                        options.sellerName.add(service.seller.name);
                    }
                } else if (currentFilterKey === 'publishPrice') {
                    if (service.publish_price !== null && service.publish_price !== undefined) {
                        options.publishPrice.add(service.publish_price);
                    }
                } else if (currentFilterKey.startsWith('provider_')) {
                    const attrKey = currentFilterKey.replace('provider_', '');
                    service.providers?.forEach(provider => {
                        provider.self_attrs?.forEach(attr => {
                            if (attr.key === attrKey && attr.value !== null && attr.value !== undefined && attr.value !== '') {
                                options[currentFilterKey].add(attr.value);
                            }
                        });
                    });
                } else {
                    const value = getAttrValue(service, currentFilterKey);
                    if (value !== null && value !== undefined && value !== '') {
                        options[currentFilterKey].add(value);
                    }
                }
            });
        });

        // Convert sets to sorted arrays
        const sortedOptions: Record<string, any[]> = {};
        Object.entries(options).forEach(([key, valueSet]) => {
            const values = Array.from(valueSet);
            // Sort numbers numerically, everything else alphabetically
            if (values.every(v => !isNaN(Number(v)))) {
                sortedOptions[key] = values.sort((a, b) => Number(a) - Number(b));
            } else {
                sortedOptions[key] = values.sort();
            }
        });

        return sortedOptions;
    }, [services, filters, allAttributeKeys, providerAttributeKeys, allFilterOptions, publicPage]);

    const [visibleCount, setVisibleCount] = useState(20);
    const LOAD_MORE_INCREMENT = 20;

    const filteredAndSortedServices = useMemo(() => {
        let filtered = services
            .filter(service => {
                // Check each filter
                for (const [key, filterValues] of Object.entries(filters)) {
                    if (filterValues.length > 0) {
                        if (key === 'businessName') {
                            const hasMatchingBusiness = service.providers?.some(provider =>
                                filterValues.includes(provider.name)
                            );
                            if (!hasMatchingBusiness) return false;
                        } else if (key === 'sellerName') {
                            if (!filterValues.includes(service.seller?.name)) return false;
                        } else if (key === 'publishPrice') {
                            if (!filterValues.includes(service.publish_price)) return false;
                        } else if (key.startsWith('provider_')) {
                            // Handle provider attribute filters
                            const attrKey = key.replace('provider_', '');
                            const hasMatchingProvider = service.providers?.some(provider => {
                                const providerAttr = provider.self_attrs?.find(attr => attr.key === attrKey);
                                return providerAttr && filterValues.includes(providerAttr.value);
                            });
                            if (!hasMatchingProvider) return false;
                        } else {
                            const attrValue = getAttrValue(service, key);
                            if (!filterValues.includes(attrValue)) return false;
                        }
                    }
                }

                return true;
            })
            .filter(service => {
                const title = multiWordSearch(ServiceTitleStatic(service), searchText) || false;
                const _id = service.id.toString() === searchText.trim();
                return title || _id;
            })

        // Then sort if needed
        if (sortConfig.key && sortConfig.direction) {
            filtered = [...filtered].sort((a, b) => {
                let aValue: any;
                let bValue: any;

                // Handle special columns
                if (sortConfig.key === 'service' || sortConfig.key === 'name') {
                    aValue = ServiceTitleStatic(a) || '';
                    bValue = ServiceTitleStatic(b) || '';
                } else if (sortConfig.key === 'city') {
                    aValue = a.city || '';
                    bValue = b.city || '';
                } else if (sortConfig.key === 'price') {
                    const aPrice = getServiceSellPrice(a, todayStart());
                    const bPrice = getServiceSellPrice(b, todayStart());
                    aValue = aPrice?.[0]?.default_amount || 0;
                    bValue = bPrice?.[0]?.default_amount || 0;
                } else if (sortConfig.key === 'providers') {
                    aValue = a.providers?.[0]?.name || '';
                    bValue = b.providers?.[0]?.name || '';
                } else if (sortConfig.key === 'sold_by') {
                    aValue = a.seller?.name || '';
                    bValue = b.seller?.name || '';
                } else if (sortConfig.key === 'publish_price') {
                    aValue = a.publish_price;
                    bValue = b.publish_price;
                } else if (sortConfig.key?.startsWith('provider_')) {
                    // Handle provider attribute sorting
                    const attrKey = sortConfig.key.replace('provider_', '');
                    const aProvider = a.providers?.[0];
                    const bProvider = b.providers?.[0];
                    const aAttr = aProvider?.self_attrs?.find(attr => attr.key === attrKey);
                    const bAttr = bProvider?.self_attrs?.find(attr => attr.key === attrKey);
                    aValue = aAttr?.value || '';
                    bValue = bAttr?.value || '';
                } else {
                    aValue = getAttrValue(a, sortConfig.key) || '';
                    bValue = getAttrValue(b, sortConfig.key) || '';
                }

                // Handle null/undefined
                if (aValue === null || aValue === undefined || aValue === '') return 1;
                if (bValue === null || bValue === undefined || bValue === '') return -1;

                // Sort numbers
                if (typeof aValue === 'number' && typeof bValue === 'number') {
                    return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
                }

                // Sort booleans
                if (typeof aValue === 'boolean' && typeof bValue === 'boolean') {
                    const aNum = aValue ? 1 : 0;
                    const bNum = bValue ? 1 : 0;
                    return sortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum;
                }

                // Sort strings
                const aStr = String(aValue).toLowerCase();
                const bStr = String(bValue).toLowerCase();

                if (sortConfig.direction === 'asc') {
                    return aStr.localeCompare(bStr);
                } else {
                    return bStr.localeCompare(aStr);
                }
            });
        }

        return filtered;
    }, [services, filters, searchText, sortConfig]);

    // Reset visible count when filters or sort change
    useEffect(() => {
        setVisibleCount(LOAD_MORE_INCREMENT);
    }, [filters, searchText, sortConfig]);

    const visibleServices = useMemo(() => {
        return filteredAndSortedServices.slice(0, visibleCount);
    }, [filteredAndSortedServices, visibleCount]);

    const handleLoadMore = () => {
        setVisibleCount(prev => prev + LOAD_MORE_INCREMENT);
    };

    const handleSort = useCallback((key: string) => {
        let direction: 'asc' | 'desc' | null = 'asc';

        if (sortConfig.key === key) {
            if (sortConfig.direction === 'asc') {
                direction = 'desc';
            } else if (sortConfig.direction === 'desc') {
                direction = null;
            }
        }

        setSortConfig({ key: direction ? key : null, direction });
    }, [sortConfig.key, sortConfig.direction]);

    const getSortIcon = (key: string) => {
        if (sortConfig.key !== key) {
            return <IconSelector size={14} className="opacity-30" />;
        }
        if (sortConfig.direction === 'asc') {
            return <IconChevronUp size={14} className="text-blue-600" />;
        }
        if (sortConfig.direction === 'desc') {
            return <IconChevronDown size={14} className="text-blue-600" />;
        }
        return <IconSelector size={14} className="opacity-30" />;
    };

    const toggleFilter = useCallback((filterType: string, value: any) => {
        setFilters(prev => {
            const currentFilter = prev[filterType] || [];
            const isIncluded = currentFilter.includes(value);

            return {
                ...prev,
                [filterType]: isIncluded
                    ? currentFilter.filter(item => item !== value)
                    : [...currentFilter, value]
            };
        });
    }, []);

    const serviceCategory = services[0]?.category;
    const template = serviceCategory ? SERVICE_TEMPLATES[serviceCategory as keyof typeof SERVICE_TEMPLATES] : null;

    const ATTR_TEXT_MAX_LENGTH = 25;
    const formatAttributeValue = (value: any, key: string): { formattedValue: string, strong: boolean, semibold: boolean } => {
        const attrDef = template?.self_attrs?.find(attr => attr.key === key);
        const result = {
            formattedValue: '',
            strong: attrDef?.strong ?? false,
            semibold: attrDef?.semibold ?? false
        };
        if (value === null || value === undefined || value === '') {
            return result;
        };
        if (attrDef?.type === 'boolean') {
            result.formattedValue = value ? t('yes') : t('no');
        }
        else if (attrDef?.type === 'time') {
            result.formattedValue = formatTime(String(value));
        }
        else if (String(value).length > ATTR_TEXT_MAX_LENGTH) {
            result.formattedValue = String(value).substring(0, ATTR_TEXT_MAX_LENGTH) + '...';
        } else {
            result.formattedValue = String(value);
        }
        return result;
    };

    const gridTemplateColumns = `
        ${!onSelect ? '120px' : ''}
        minmax(200px, 1fr)
        120px
        150px
        150px
        150px
        200px
        100px
    `;

    const TableView = () => (
        <div className="h-fit flex-1 min-w-0">
            <div className="solid-box-border p-0 overflow-x-auto max-w-full">
                <div style={{ minWidth: 'max-content' }}>
                    {/* Header */}
                    <div
                        className="table-grid-header"
                        style={{ gridTemplateColumns }}>

                        {!onSelect && (
                            <div className="sticky bg-gray-50/90 left-0 z-10 flex items-center justify-start">
                                <CustomCheckbox
                                    checked={selectedServices.length === filteredAndSortedServices.length}
                                    onChange={(e) => handleSelectAllServices(e)}
                                />
                            </div>
                        )}

                        <div onClick={() => handleSort('name')}>
                            <div className="flex items-center gap-1">
                                {t('title')}
                                {getSortIcon('name')}
                            </div>
                        </div>
                        <div onClick={() => handleSort('city')}>
                            <div className="flex items-center gap-1">
                                {t('city')}
                                {getSortIcon('city')}
                            </div>
                        </div>
                        <div onClick={() => handleSort('price')}>
                            <div className="flex items-center gap-1 justify-end">
                                {t('price')}
                                {getSortIcon('price')}
                            </div>
                        </div>
                        <div onClick={() => handleSort('cost')}>
                            <div className="flex items-center gap-1 justify-end">
                                {t('cost')}
                                {getSortIcon('cost')}
                            </div>
                        </div>
                        <div onClick={() => handleSort('sold_by')}>
                            <div className="flex items-center gap-1">
                                {t('sold_by_long')}
                                {getSortIcon('sold_by')}
                            </div>
                        </div>
                        <div onClick={() => handleSort('providers')}>
                            <div className="flex items-center gap-1">
                                {t('operated_by')}
                                {getSortIcon('providers')}
                            </div>
                        </div>
                        <div onClick={() => handleSort('publish_price')}>
                            <div className="flex items-center gap-1">
                                {t('publish_online')}
                                {getSortIcon('publish_price')}
                            </div>
                        </div>
                    </div>
                    {/* Data rows */}
                    {visibleServices.map((service) => {
                        const prices = getServiceSellPrice(service, dates?.[0]);
                        const cost = getServiceSellCost(service, dates?.[0]);

                        const rowContent = (
                            <div
                                className={`
                                    table-grid-row
                                    group
                                    ${selectedServices.includes(service.id) ? 'bg-gray-50' : 'bg-white'}
                                `}
                                style={{ gridTemplateColumns }}>


                                {!onSelect && (
                                    <div className="sticky left-0 z-10 flex items-center justify-between gap-1">
                                        <CustomCheckbox
                                            checked={selectedServices.includes(service.id)}
                                            onChange={(e) => handleSelectService(service.id, e)}
                                        />
                                        <Link
                                            to={`${SERVICES_PATH}/${service.id}`}
                                            style={{ textDecoration: 'none', color: 'inherit' }}
                                            className="hover:bg-gray-100 rounded-md p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                        >
                                            <IconEdit size={20} stroke={1.5} color='#888' />
                                        </Link>
                                        <span className="text-right text-gray-400 text-xxs">
                                            <span className="text-gray-400 text-[0.5rem]">#</span>{service.id}
                                        </span>
                                    </div>
                                )}

                                {/* title */}
                                <div className="font-medium">
                                    {ServiceTitleStatic(service)}
                                </div>

                                {/* city */}
                                <div>
                                    {service.city || '-'}
                                </div>

                                {/* price */}
                                <div className="flex flex-col gap-1 justify-start items-end">
                                    {prices.length > 0 ? (
                                        <>
                                            {prices.map(price => (
                                                <Tooltip key={price.id} label={formatDayShort(price?.start_day) + ' - ' + formatDayShort(price?.end_day)}>
                                                    <div className="flex gap-0.5 justify-end">
                                                        <div className="font-medium">{formatPrice(price.default_amount)}</div>
                                                        <div className="text-gray-400 font-bold">{price.default_currency}</div>
                                                    </div>
                                                </Tooltip>
                                            ))}
                                            <div className="text-gray-400 text-xxs italic flex items-center gap-1">
                                                <div>
                                                    <span className="font-semibold">{t(`price_by_${service.price_by}`)}</span>
                                                </div>
                                            </div>
                                        </>
                                    ) : (
                                        <span className="text-gray-300 text-sm">
                                            {t('no_price')}
                                        </span>
                                    )}
                                </div>

                                {/* cost */}
                                <div className="flex flex-col gap-1 justify-start items-end">
                                    {cost ? (
                                        <>
                                            <Tooltip label={formatDayShort(cost?.start_day) + ' - ' + formatDayShort(cost?.end_day)}>
                                                <div className="flex gap-0.5 justify-end">
                                                    <div className="font-medium">{formatPrice(cost.default_amount)}</div>
                                                    <div className="text-gray-400 font-bold">{cost.default_currency}</div>
                                                </div>
                                            </Tooltip>
                                            <div className="text-gray-400 text-xxs italic">
                                                <span className="font-semibold">{t(`price_by_${service.cost_by}`)}</span>
                                            </div>
                                        </>
                                    ) : (
                                        <span className="text-gray-300 text-sm">
                                            {t('no_cost')}
                                        </span>
                                    )}
                                </div>

                                {/* sold by */}
                                <div>
                                    <div className="py-0.5 px-1 rounded-md bg-orange-50 w-fit">
                                        {service.seller?.name || '-'}
                                    </div>
                                </div>

                                {/* providers */}
                                <div className="space-y-1">
                                    {service.providers.map((provider, index) => (
                                        <div
                                            key={provider.id + '_' + index}
                                            className="text-gray-600 py-0.5 px-1 rounded-md bg-gray-100 w-fit">
                                            <Link
                                                to={`${BUSINESSES_PATH}/${provider.id}`}
                                            >
                                                {provider.name}
                                            </Link>
                                        </div>
                                    ))}
                                </div>

                                {/* publish online */}
                                <div>
                                    <Badge
                                        color={service.publish_price ? 'green' : 'red'}
                                        size="sm"
                                    >
                                        {service.publish_price ? t('yes') : t('no')}
                                    </Badge>
                                </div>
                            </div>
                        );
                        return (
                            <div
                                key={service.id}
                                onClick={onSelect ? () => onSelect(service) : null}
                                className={onSelect ? 'block cursor-pointer' : ''}>
                                {rowContent}
                            </div>
                        )
                    })}
                </div>
            </div>
            {visibleCount < filteredAndSortedServices.length && (
                <div className="flex justify-center mt-4">
                    <Button onClick={handleLoadMore} variant="light">
                        {t('load_more')}
                    </Button>
                </div>
            )}
        </div>
    )

    const ListView = () => (
        <div className="space-y-4">
            {visibleServices.map(service => (
                <div key={service.id}>
                    {publicPage ? (
                        <ServiceCard
                            service={service}
                            onSelect={onSelect}
                            start_day={dates?.[0]}
                            client={client}
                        />
                    ) : (
                        <ServiceCardPrivate
                            showProviders={true}
                            onSelect={onSelect}
                            service={service}
                            start_day={dates?.[0]}
                            end_day={dates?.[1]}
                        />
                    )}
                </div>
            ))}
            {visibleCount < filteredAndSortedServices.length && (
                <div className="flex justify-center p-4">
                    <Button onClick={handleLoadMore} variant="light">
                        {t('load_more')}
                    </Button>
                </div>
            )}
        </div>
    )

    const getFilterLabel = useCallback((key: string): string => {
        if (key === 'businessName') return t('operated_by');
        if (key === 'sellerName') return t('sold_by_long');
        if (key === 'publishPrice') return t('publish_price');
        if (key.startsWith('provider_')) return t(key.replace('provider_', ''));
        return t(key);
    }, [t]);

    const formatFilterValue = useCallback((value: any): string => {
        if (typeof value === 'boolean') return value ? t('yes') : t('no');
        return String(value);
    }, [t]);

    const sortOptions = useMemo(() => [
        { value: 'price', label: t('price') },
        { value: 'name', label: t('name') }
    ], [t]);

    const viewOptions = useMemo(() => [
        {
            value: 'table',
            label: (
                <div className="flex items-center gap-1">
                    <IconTable size={14} />
                    <span>{t('table')}</span>
                </div>
            )
        },
        {
            value: 'list',
            label: (
                <div className="flex items-center gap-1">
                    <IconList size={14} />
                    <span>{t('list')}</span>
                </div>
            )
        }
    ], [t]);

    const handleSortChange = useCallback((value: string | null) => {
        if (value) {
            setSortConfig({ key: value, direction: 'asc' });
        } else {
            setSortConfig({ key: null, direction: null });
        }
    }, []);

    const clearFilters = useCallback(() => {
        setFilters({});
    }, []);

    const summaryContent = useMemo(() => {
        const activeFiltersCount = Object.values(filters).reduce((count, filterValues) => count + filterValues.length, 0);

        return (
            <div className="mb-4 space-y-3">
                {/* Top row: Results count and controls */}
                <div className="flex gap-4 items-center justify-between">
                    <div className="flex items-center gap-4">
                        <h2 className="text-sm font-semibold text-gray-900">
                            {filteredAndSortedServices.length} {filteredAndSortedServices.length === 1 ? t('result') : t('results')}
                        </h2>
                        {selectedServices.length > 0 && (
                            <div className="flex items-center gap-2">
                                <span className="text-sm text-gray-600">
                                    {selectedServices.length} {t('selected')}
                                </span>
                                <BulkServiceEditModal
                                    services={services.filter(s => selectedServices.includes(s.id as number))}
                                    target={
                                        <Button
                                            size="xs"
                                            radius="sm"
                                            color="black"
                                        >
                                            {t('edit_selected')}
                                        </Button>
                                    }
                                />
                            </div>
                        )}
                    </div>
                    <div className="flex items-center gap-1">
                        <Select
                            className="w-fit"
                            placeholder={t('sort_by')}
                            value={sortConfig.key || ''}
                            onChange={handleSortChange}
                            data={sortOptions}
                        />
                        {!publicPage && (
                            <SegmentedControl
                                size="lg"
                                radius="xl"
                                value={view}
                                onChange={(value) => setView(value as 'table' | 'list')}
                                data={viewOptions}
                            />
                        )}
                    </div>
                </div>

                {/* Active filters row */}
                {activeFiltersCount > 0 && (
                    <div className="flex flex-wrap items-center gap-2">
                        {Object.entries(filters).map(([filterKey, filterValues]) =>
                            filterValues.map((value) => (
                                <div
                                    key={`${filterKey}-${value}`}
                                    className="text-sm solid-round-red-hover flex gap-4 justify-between items-center"
                                    onClick={() => toggleFilter(filterKey, value)}
                                >
                                    <div className="flex items-center gap-1">
                                        <div className="font-light">{getFilterLabel(filterKey)}:</div>
                                        <div className="font-semibold">{formatFilterValue(value)}</div>
                                    </div>
                                    <IconX size={14} stroke={3} color='#aaa' />
                                </div>
                            ))
                        )}
                        <Button
                            variant="light"
                            size="xs"
                            radius="xl"
                            color="black"
                            onClick={clearFilters}
                        >
                            {t('clear_filters')}
                        </Button>
                    </div>
                )}
                <SearchInput
                    size="md"
                    onSearch={setSearchText}
                    initialValue={searchText}
                />
            </div>
        );
    }, [filters, filteredAndSortedServices.length, selectedServices, services, sortConfig.key, view, sortOptions, viewOptions]);

    return (
        <div className={`flex gap-4 ${isMobile ? 'flex-col' : 'flex-row'}`}>

            {!isMobile && (
                <ServiceFiltersColumn
                    className="w-1/4 max-w-80 solid-box-border p-0"
                    defaultExpanded={true}
                    filters={filters}
                    allFilterOptions={allFilterOptions}
                    availableFilterOptions={availableFilterOptions}
                    allAttributeKeys={allAttributeKeys}
                    providerAttributeKeys={providerAttributeKeys}
                    onToggleFilter={toggleFilter}
                />
            )}

            {isMobile && (
                <>
                    <Button
                        onClick={() => setFiltersModalOpened(true)}
                        color="black"
                        radius="xl"
                        size="xs"
                    >
                        {t('filters')}
                    </Button>
                    <Modal
                        opened={filtersModalOpened}
                        onClose={() => setFiltersModalOpened(false)}
                        title={<div className="text-lg font-semibold text-gray-800 text-center">{t('filters')}</div>}
                    >
                        <ServiceFiltersColumn
                            className="w-full solid-box-border p-0"
                            defaultExpanded={true}
                            filters={filters}
                            allFilterOptions={allFilterOptions}
                            availableFilterOptions={availableFilterOptions}
                            allAttributeKeys={allAttributeKeys}
                            providerAttributeKeys={providerAttributeKeys}
                            onToggleFilter={toggleFilter}
                        />
                    </Modal>
                </>
            )}

            {
                (services.length > 0 && view === 'table') && !publicPage
                    ? <div className="flex-1 min-w-0">
                        {summaryContent}
                        <TableView />
                    </div>
                    : null
            }
            {
                (services.length > 0 && view === 'list') || publicPage
                    ? <div className="flex-1 min-w-0">
                        {summaryContent}
                        <ListView />
                    </div>
                    : null
            }
        </div>
    );
}